import type { UpdateProgress } from '@infra/update/UpdateService';

const UPDATE_STAGE_ORDER = [
  'checking',
  'backing_up',
  'downloading',
  'verifying',
  'applying',
  'restarting',
] as const;

const INSTALL_STAGES = new Set([
  'backing_up',
  'downloading',
  'verifying',
  'applying',
  'restarting',
]);

const STATUS_KINDS = ['busy', 'ok', 'available', 'error'] as const;

function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

const STAGE_PERCENT: Partial<Record<UpdateProgress['stage'], number>> = {
  backing_up: 10,
  downloading: 0,
  verifying: 90,
  applying: 95,
  restarting: 100,
};

/** Pourcentage affiché (0–100). Indéfini = barre indéterminée (vérification seule). */
export function resolveUpdateBarPercent(progress: UpdateProgress): number | undefined {
  const raw = progress.percent;
  const parsed = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  if (Number.isFinite(parsed)) return Math.max(0, Math.min(100, parsed));
  return STAGE_PERCENT[progress.stage];
}

export function isUpdateCheckUpToDate(progress: UpdateProgress): boolean {
  if (progress.stage !== 'done') return false;
  if (progress.upToDate === true) return true;
  if (progress.upToDate === false) return false;
  return (
    /déjà à jour|à jour/i.test(progress.message ?? '') &&
    !/disponible/i.test(progress.message ?? '')
  );
}

export function paintUpdateProgress(section: HTMLElement, progress: UpdateProgress): void {
  const track = section.querySelector<HTMLElement>('#update-track');
  const notice = section.querySelector<HTMLElement>('#update-restart-notice');
  const bar = section.querySelector<HTMLElement>('#update-progressbar');
  const fill = section.querySelector<HTMLElement>('#update-bar-fill');
  const stageLabel = section.querySelector('#update-stage-label');
  const percentEl = section.querySelector('#update-percent');
  const statusEl = section.querySelector('#update-status');

  const failed = progress.stage === 'failed';
  const upToDate = isUpdateCheckUpToDate(progress);
  const available = progress.stage === 'done' && !upToDate;
  const busy = progress.stage !== 'done' && !failed;
  const hideTrack = upToDate;
  const showNotice = available || INSTALL_STAGES.has(progress.stage);

  if (track) track.hidden = hideTrack;
  if (notice) notice.hidden = !showNotice;

  const messages: Record<string, string> = {
    checking: 'Vérification de la version disponible…',
    downloading: 'Téléchargement de la mise à jour…',
    verifying: 'Contrôle d’intégrité du paquet…',
    backing_up: 'Sauvegarde chiffrée en cours…',
    applying: 'Installation de la mise à jour…',
    restarting: 'Nexxus ERP va redémarrer après l’installation.',
    done: progress.message ?? 'Terminé',
    failed: progress.message ?? 'Échec de la mise à jour.',
  };
  const message = messages[progress.stage] ?? progress.message;
  if (statusEl && message) {
    statusEl.textContent = message;
    statusEl.classList.remove(...STATUS_KINDS.map((kind) => `nx-update-status--${kind}`));
    const kind = failed ? 'error' : upToDate ? 'ok' : available ? 'available' : 'busy';
    statusEl.classList.add(`nx-update-status--${kind}`);
  }
  if (stageLabel && message) stageLabel.textContent = message;

  const idx = available
    ? 1
    : UPDATE_STAGE_ORDER.indexOf(progress.stage as (typeof UPDATE_STAGE_ORDER)[number]);
  section.querySelectorAll('#update-steps li').forEach((li) => {
    const stage = li.getAttribute('data-stage') ?? '';
    const stageIdx = UPDATE_STAGE_ORDER.indexOf(stage as (typeof UPDATE_STAGE_ORDER)[number]);
    li.classList.toggle('is-current', busy && stage === progress.stage);
    li.classList.toggle('is-done', idx >= 0 && stageIdx >= 0 && stageIdx < idx);
    li.classList.toggle('is-failed', failed);
  });

  const pct = resolveUpdateBarPercent(progress);
  const determinate = busy && pct !== undefined;
  fill?.classList.toggle('is-indeterminate', busy && !determinate);
  const width = determinate && pct !== undefined ? pct : failed ? 100 : 0;
  if (fill) {
    if (determinate || failed) {
      fill.style.transform = 'none';
      fill.style.animation = 'none';
    } else {
      fill.style.removeProperty('transform');
      fill.style.removeProperty('animation');
    }
    fill.style.width = `${width}%`;
  }
  if (bar) {
    bar.setAttribute('aria-valuenow', String(width));
    bar.classList.toggle('is-failed', failed);
  }
  if (percentEl) {
    const received =
      typeof progress.receivedBytes === 'number' ? progress.receivedBytes : undefined;
    const total = typeof progress.totalBytes === 'number' ? progress.totalBytes : undefined;
    if (!busy) {
      percentEl.textContent = '';
    } else if (determinate && total !== undefined && total > 0 && received !== undefined) {
      percentEl.textContent = `${pct} % · ${humanSize(received)} / ${humanSize(total)}`;
    } else if (determinate) {
      percentEl.textContent = `${pct} %`;
    } else if (received !== undefined && received > 0) {
      percentEl.textContent = `${humanSize(received)} reçus`;
    } else {
      percentEl.textContent = 'En cours…';
    }
  }
}
